import { useEffect, useState } from 'react'
import { X, MoreHorizontal, ShoppingCart, Check } from 'lucide-react'
import type { SavedRecipeDetail } from '../../lib/savedRecipeTypes'
import { RecipeServingsControl } from './RecipeServingsControl'
import { RecipeIngredientRow } from './RecipeIngredientRow'
import { RecipeOverflowMenu } from './RecipeOverflowMenu'
import { InstructionsSection } from './InstructionsSection'

type Props = {
  recipe:           SavedRecipeDetail
  onClose:          () => void
  onServingsChange: (next: number) => void
  onShopForThis:    () => Promise<void> | void
  onIMadeThis:      () => Promise<void> | void
  onDelete:         () => Promise<void> | void
  onSubstitute:     (ingredientId: string) => Promise<void> | void
  onToggleHasIt:    (ingredientId: string, next: boolean) => Promise<void> | void
}

export function SavedRecipeModal({
  recipe,
  onClose,
  onServingsChange,
  onShopForThis,
  onIMadeThis,
  onDelete,
  onSubstitute,
  onToggleHasIt,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [shopping, setShopping] = useState(false)
  const [making,   setMaking]   = useState(false)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !menuOpen) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen, onClose])

  const haveCount = recipe.ingredients.filter(i => i.hasIt).length
  const needCount = recipe.ingredients.length - haveCount

  async function handleShop() {
    setShopping(true)
    try { await onShopForThis() } finally { setShopping(false) }
  }
  async function handleMake() {
    setMaking(true)
    try { await onIMadeThis() } finally { setMaking(false) }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="recipe-modal-title"
      className="fixed inset-0 z-50 flex items-end justify-center"
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      <div
        className="relative w-full max-w-[460px] flex flex-col bg-cream rounded-t-[28px] animate-[savedRecipeSlideUp_280ms_ease-out]"
        style={{ maxHeight: '90dvh' }}
      >
        {/* Handle */}
        <div className="flex-shrink-0 pt-3 pb-2 flex justify-center">
          <div className="h-1 w-10 rounded-full bg-[#D4C4A8]" />
        </div>

        {/* Fixed header */}
        <div className="flex-shrink-0 px-[18px] pb-[14px]">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
                Saved recipe
              </div>
              <h1
                id="recipe-modal-title"
                className="font-serif text-[24px] font-medium text-ink leading-[1.1] tracking-[-0.02em] break-words"
              >
                {renderItalicTail(recipe.name)}
              </h1>
              <p className="font-serif text-[13px] italic text-ink-muted mt-1 leading-[1.35]">
                {recipe.ingredients.length} ingredients
                {recipe.prepMinutes > 0 ? ` · ${recipe.prepMinutes} min` : ''}
                {' · '}serves {recipe.servings}
              </p>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              <button
                onClick={() => setMenuOpen(true)}
                aria-label="More options"
                className="h-8 w-8 rounded-[10px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted"
              >
                <MoreHorizontal className="h-[14px] w-[14px]" strokeWidth={1.8} />
              </button>
              <button
                onClick={onClose}
                aria-label="Close"
                className="h-8 w-8 rounded-[10px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted"
              >
                <X className="h-[14px] w-[14px]" strokeWidth={1.8} />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable body — min-h-0 is the magic that makes overflow-y-auto actually scroll */}
        <div className="flex-1 min-h-0 overflow-y-auto px-[18px] pb-2">

          {/* Inline stat tiles — forest "you have" + cream-warm "you need" */}
          <div className="flex gap-2 mb-[18px]">
            <div className="flex-1 rounded-[12px] bg-[#E0EBE2] px-3 py-[10px]">
              <div className="flex items-baseline gap-1.5">
                <span className="font-serif text-[22px] font-medium text-forest leading-none">
                  {haveCount}
                </span>
                <span className="text-[11px] italic text-forest">you have</span>
              </div>
            </div>
            <div className="flex-1 rounded-[12px] bg-[#FDF4EC] px-3 py-[10px]">
              <div className="flex items-baseline gap-1.5">
                <span className="font-serif text-[22px] font-medium text-terracotta leading-none">
                  {needCount}
                </span>
                <span className="text-[11px] italic text-terracotta">you need</span>
              </div>
            </div>
          </div>

          <RecipeServingsControl
            servings={recipe.servings}
            onChange={onServingsChange}
          />

          <div className="mb-2 mt-[18px] flex items-baseline gap-2 px-0.5">
            <h2 className="font-serif text-[14px] italic text-ink">Ingredients</h2>
            <div className="flex-1 h-px bg-border-sand" />
            <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted">
              {recipe.ingredients.length}
            </span>
          </div>

          <div className="overflow-hidden rounded-[16px] border border-border-sand bg-paper">
            {recipe.ingredients.map((ing, idx) => (
              <RecipeIngredientRow
                key={ing.id}
                ingredient={ing}
                isLast={idx === recipe.ingredients.length - 1}
                onToggleHasIt={onToggleHasIt}
                onSubstitute={() => onSubstitute(ing.id)}
              />
            ))}
          </div>

          <p className="mt-[10px] mb-2 px-1 text-center font-serif text-[11px] italic text-ink-muted leading-[1.4]">
            Tap any circle to mark an ingredient as have or need.
          </p>

          {recipe.instructions && (
            <InstructionsSection instructions={recipe.instructions} />
          )}

        </div>

        {/* Sticky CTA footer */}
        <div className="flex-shrink-0 px-[18px] pt-3 pb-5 bg-gradient-to-b from-transparent via-cream/80 to-cream">
          {needCount > 0 && (
            <button
              onClick={handleShop}
              disabled={shopping}
              className="w-full mb-2 rounded-[14px] bg-forest text-forest-ink py-[14px] text-[14px] font-bold tracking-[-0.01em] flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              <ShoppingCart className="h-[14px] w-[14px]" strokeWidth={1.8} />
              {shopping ? 'Adding…' : `Shop for this — add ${needCount} to list`}
            </button>
          )}
          <button
            onClick={handleMake}
            disabled={making || haveCount === 0}
            className="w-full rounded-[14px] border border-border-sand bg-paper text-ink py-3 text-[13px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <Check className="h-[14px] w-[14px] text-forest" strokeWidth={2} />
            {making
              ? 'Updating pantry…'
              : haveCount === 0
              ? "You don't have any ingredients yet"
              : `I made this — deplete ${haveCount} ingredient${haveCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      {menuOpen && (
        <RecipeOverflowMenu
          onClose={() => setMenuOpen(false)}
          onDelete={async () => {
            setMenuOpen(false)
            await onDelete()
          }}
        />
      )}

      <style>{`
        @keyframes savedRecipeSlideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

// Render "Recipe Title" with the last word italicized, editorial-style.
function renderItalicTail(title: string): React.ReactNode {
  const parts = title.trim().split(/\s+/)
  if (parts.length <= 1) return <em className="italic">{title}</em>
  const head = parts.slice(0, -1).join(' ')
  const tail = parts[parts.length - 1]
  return (
    <>
      {head} <em className="italic">{tail}</em>
    </>
  )
}
